import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentCoords } from 'types/geometric/geckoComponentCoords'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { requiredNumberSchema, requiredStringSchema } from 'utils/zodHelpers'

export interface GeckoShipDeckChockJSON {
  type: GeckoFeatureModelType.ShipDeckChock
  name: string
  feature_geometry: {
    x: number
    y: number
    nominal_fitting_size: ChockNominalFittingSize
  }
}

export enum ChockNominalFittingSize {
  US6 = 'US6',
  US10 = 'US10',
  US13 = 'US13',
  US16 = 'US16',
  US20 = 'US20',
  US24 = 'US24',
}

export const NewGeckoShipDeckChockJSON: GeckoShipDeckChockJSON = {
  type: GeckoFeatureModelType.ShipDeckChock,
  name: 'New Ship Deck Chock',
  feature_geometry: {
    x: 0,
    y: 0,
    nominal_fitting_size: ChockNominalFittingSize.US6,
  },
}

export class GeckoShipDeckChock extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.ShipDeckChock
  readonly nominalFittingSize: ChockNominalFittingSize
  readonly geometry: GeckoComponentCoords

  private constructor(
    id: string,
    name: string,
    nominalFittingSize: ChockNominalFittingSize,
    geometry: GeckoComponentCoords
  ) {
    super(id)
    this.name = name
    this.nominalFittingSize = nominalFittingSize
    this.geometry = geometry
  }

  public static CreateFromJSON(
    componentID: string,
    chockID: string,
    chockJSON: GeckoShipDeckChockJSON
  ): GeckoShipDeckChock {
    const featureGeometryData = chockJSON.feature_geometry as Record<
      string,
      number | string
    >
    const chockGeometry = GeckoComponentCoords.CreateFromValues(
      componentID,
      featureGeometryData.x as number,
      GeckoMeasurementUnit.Meters,
      featureGeometryData.y as number,
      GeckoMeasurementUnit.Meters
    )

    const chockName = chockJSON.name as string

    const nominalFittingSize =
      featureGeometryData.nominal_fitting_size as ChockNominalFittingSize

    return new GeckoShipDeckChock(
      chockID,
      chockName,
      nominalFittingSize,
      chockGeometry
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoShipDeckChockJSON>
  ): Map<string, GeckoShipDeckChock> {
    return Object.keys(recordsJSON).reduce((resultObj, id) => {
      resultObj.set(
        id,
        GeckoShipDeckChock.CreateFromJSON(componentID, id, recordsJSON[id])
      )
      return resultObj
    }, new Map<string, GeckoShipDeckChock>())
  }

  asJSON() {
    return generateChock(
      this.name,
      this.geometry.x,
      this.geometry.y,
      this.nominalFittingSize
    )
  }
}

export function generateChock(
  name: string,
  x: GeckoMeasurement,
  y: GeckoMeasurement,
  nominalFittingSize: ChockNominalFittingSize
): GeckoShipDeckChockJSON {
  return {
    type: GeckoFeatureModelType.ShipDeckChock,
    name: name,
    feature_geometry: {
      x: x.toNumber(GeckoMeasurementUnit.Meters),
      y: y.toNumber(GeckoMeasurementUnit.Meters),
      nominal_fitting_size: nominalFittingSize,
    },
  }
}

export const ShipDeckChockFormFields = (chockJSON: GeckoShipDeckChockJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Chock Name',
      value: chockJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'x',
      label: 'X Position',
      value: chockJSON.feature_geometry.x,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y',
      label: 'Y Position',
      value: chockJSON.feature_geometry.y,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'nominal_fitting_size',
      label: 'Nominal Fitting Size',
      value: chockJSON.feature_geometry.nominal_fitting_size,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      options: Object.keys(ChockNominalFittingSize).map((key) => ({
        key: ChockNominalFittingSize[
          key as keyof typeof ChockNominalFittingSize
        ],
        value: key,
        label: key.replace('US', 'US '),
      })),
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
  ]
}

export const ShipDeckChockJSONFromFormFields = (formFields: FormField[]) => {
  return generateChock(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    getFormFieldByDataKey(formFields, 'nominal_fitting_size')
      ?.value as ChockNominalFittingSize
  )
}

export const ShipDeckChockFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.ShipDeckChock,
  title: 'Chocks',
  singularName: 'Chock',
  fields: ['x', 'y', 'nominal_fitting_size'],
}
